/** /tour-operators/new/partner — the create wizard's partner path (dev-platform metaplan G1; GATE P1).
 *
 *  One form, one POST: the integration partner, its first technical contact and its
 *  stand-alone sandbox (`<partner code>sbx`, kind=sandbox, onboarding profile
 *  partner_sandbox, no default key). Creation lands on the sandbox's setup, on the
 *  Partner step: record the contract first — everything else waits on it. */
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import * as partnersApi from '@/api/partners';
import { PARTNER_KIND_LABELS, SANDBOX_INTEGRATIONS, type PartnerKind } from '@/api/partners';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { defaultLang } from '@/utils/defaultLang';

import { LANGUAGES } from '../tour-operators/wizard/languages';
import { partnerErrorMessage } from './partnerErrors';

const KINDS = Object.keys(PARTNER_KIND_LABELS) as [PartnerKind, ...PartnerKind[]];
const LANG_MAP = new Map(LANGUAGES.map((l) => [l.code, l.label]));
const langLabel = (code: string) => (LANG_MAP.get(code) ? `${code} — ${LANG_MAP.get(code)}` : code);

const CreateSchema = z.object({
  code: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]{2,50}$/, '2 to 50 lowercase letters and digits.'),
  name: z.string().trim().min(1, 'Name is required').max(255),
  legal_name: z.string().trim().max(255),
  kind: z.enum(KINDS),
  contact_name: z.string().trim().min(1, 'A technical contact is required').max(255),
  contact_email: z.string().trim().email('Not a valid e-mail address').max(254),
  contact_phone: z.string().trim().max(64),
  lang_enabled: z.array(z.string()).min(1, 'At least one language'),
  integrations: z.array(z.string()),
});
type CreateValues = z.infer<typeof CreateSchema>;

const DEFAULTS: CreateValues = {
  code: '',
  name: '',
  legal_name: '',
  kind: 'booking_system_vendor',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  lang_enabled: ['en'],
  integrations: ['curatedplanet'],
};

/** The code check, debounced: `null` while unknown. */
function useCodeCheck(code: string) {
  const [debounced, setDebounced] = useState(code);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(code.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [code]);
  return useQuery({
    queryKey: ['partners', 'check_code', debounced],
    queryFn: () => partnersApi.checkCode(debounced),
    enabled: debounced.length >= 2,
    staleTime: 10_000,
  });
}

export function PartnerCreatePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateValues>({ resolver: zodResolver(CreateSchema), defaultValues: DEFAULTS });
  const code = useWatch({ control, name: 'code' }) ?? '';
  const codeCheck = useCodeCheck(code);
  const langs = useWatch({ control, name: 'lang_enabled' }) ?? [];

  const create = useMutation({
    mutationFn: (v: CreateValues) =>
      partnersApi.create({
        code: v.code,
        name: v.name,
        legal_name: v.legal_name,
        kind: v.kind,
        contacts: [
          { name: v.contact_name, email: v.contact_email, phone: v.contact_phone, role: 'technical', is_active: true },
        ],
        sandbox: { lang_enabled: v.lang_enabled, integrations: v.integrations },
      }),
    onSuccess: (partner) => {
      snackbar.show(`Partner "${partner.name}" and its sandbox ${partner.sandbox?.code ?? ''} created.`, 'success');
      void qc.invalidateQueries({ queryKey: ['partners'] });
      void qc.invalidateQueries({ queryKey: ['tourOperators'] });
      navigate(partner.sandbox ? `/tour-operators/${partner.sandbox.id}/setup/partner` : `/partners/${partner.id}`, {
        replace: true,
      });
    },
    onError: (e) => setError(partnerErrorMessage(e, 'Creation failed. Please try again.')),
  });

  const codeAvailable = codeCheck.data?.available;
  const codeHelper =
    errors.code?.message ??
    (codeCheck.data
      ? codeCheck.data.available
        ? `Available — the sandbox will be ${codeCheck.data.sandbox_code}.`
        : codeCheck.data.reason ?? 'Not available.'
      : 'Permanent: the sandbox is named after it (<code>sbx).');

  return (
    <Box>
      <PageHeader
        helpStory="partner-onboarding"
        title="New integration partner"
        subtitle="Creates the partner, its technical contact and its sandbox; setup continues on the sandbox's Partner step."
        actions={
          <Button component={RouterLink} to="/tour-operators/new/advanced">
            Create a tour operator instead
          </Button>
        }
      />
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit((v) => {
          setError(null);
          if (codeAvailable === false) {
            setError(codeCheck.data?.reason ?? 'The code is not available.');
            return;
          }
          create.mutate(v);
        })}
        sx={{ display: 'grid', gap: 2, maxWidth: 880, pb: 4 }}
      >
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 2 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
            Partner
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField label="Name" required {...register('name')} error={!!errors.name} helperText={errors.name?.message} />
            <TextField
              label="Code"
              required
              {...register('code')}
              error={!!errors.code || codeAvailable === false}
              helperText={codeHelper}
              InputProps={{
                endAdornment: codeCheck.isFetching ? <CircularProgress size={16} /> : undefined,
              }}
            />
            <TextField label="Legal name" {...register('legal_name')} helperText="The contracting entity." />
            <Controller
              name="kind"
              control={control}
              render={({ field }) => (
                <TextField select label="Kind" {...field}>
                  {KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {PARTNER_KIND_LABELS[k]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 2 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
            Technical contact
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The claim link for the partner’s developer key goes to this person. More contacts can be added on the
            partner’s page.
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' } }}>
            <TextField
              label="Name"
              required
              {...register('contact_name')}
              error={!!errors.contact_name}
              helperText={errors.contact_name?.message}
            />
            <TextField
              label="E-mail"
              required
              {...register('contact_email')}
              error={!!errors.contact_email}
              helperText={errors.contact_email?.message}
            />
            <TextField label="Phone" {...register('contact_phone')} />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 2 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
            Sandbox
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A stand-alone sandbox tenant: no default key (the claim link mints the first), no chat, and nothing it
            sends leaves the platform yet.
          </Typography>
          <Controller
            name="lang_enabled"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={LANGUAGES.map((l) => l.code)}
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                getOptionLabel={langLabel}
                disableCloseOnSelect
                renderTags={(value, getTagProps) =>
                  value.map((c, index) => <Chip {...getTagProps({ index })} key={c} label={langLabel(c)} size="small" />)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Languages"
                    size="small"
                    error={!!errors.lang_enabled}
                    helperText={
                      errors.lang_enabled?.message ??
                      (langs.length ? `Default language: ${langLabel(defaultLang(langs) ?? '')} (the last one).` : '')
                    }
                  />
                )}
              />
            )}
          />
          <Controller
            name="integrations"
            control={control}
            render={({ field }) => (
              <Box>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
                  Integrations
                </FormLabel>
                <FormGroup row>
                  {SANDBOX_INTEGRATIONS.map((i) => (
                    <FormControlLabel
                      key={i.code}
                      label={i.label}
                      control={
                        <Checkbox
                          checked={field.value.includes(i.code)}
                          onChange={(e) =>
                            field.onChange(
                              e.target.checked ? [...field.value, i.code] : field.value.filter((c) => c !== i.code),
                            )
                          }
                        />
                      }
                    />
                  ))}
                </FormGroup>
                <Typography variant="caption" color="text.secondary">
                  Webhooks of the XML push and Ventrata must authenticate on the sandbox.
                </Typography>
              </Box>
            )}
          />
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={create.isPending ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create partner & sandbox'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
